import api from 'services/api'

import {
  IAddressabilityFunnelResponse,
  IAddressabilityLossesDrilldownResponse,
  IAlertsResponse,
  ICaseDrilldownResponse,
  IDsarRequest,
  IDsarResponse,
  IExceptionsDrilldownResponse,
  IGovernanceResponseEnvelope,
  IMaioridadeDrilldownResponse,
  IMovementsDrilldownResponse,
  IMovementsResponse,
  INonOptantsDrilldownResponse,
  IPipelineFunnelResponse,
  IPolicyOptantsResponse,
  IScorecardResponse,
  IVigenciasDrilldownResponse,
  IWorkspacesListResponse,
} from '../types'

/**
 * Governance reports API client.
 *
 * One method per /reports/governance/* endpoint documented in
 * P1-implementation-plan Phase 3. All methods return the typed
 * response envelope; callers consume `response.data` and read
 * `source_timestamp` / `staleness_warning` via `FreshnessBadge`.
 */

export interface IScorecardQuery {
  workspaceId: string
  periodStart?: string
  periodEnd?: string
}

export interface IMovementsQuery extends IScorecardQuery {}
export interface IAddressabilityFunnelQuery {
  workspaceId: string
  snapshotStart?: string
  snapshotEnd?: string
}

type Envelope<T> = IGovernanceResponseEnvelope<T>

const base = '/reports/governance'

const toParams = (o: Record<string, string | undefined>) => {
  const out: Record<string, string> = {}
  Object.entries(o).forEach(([k, v]) => {
    if (v) out[k] = v
  })
  return out
}

export const governanceApi = {
  async scorecard(q: IScorecardQuery): Promise<Envelope<IScorecardResponse>> {
    const { data } = await api.get<Envelope<IScorecardResponse>>(
      `${base}/scorecard`,
      { params: toParams({ workspace_id: q.workspaceId, period_start: q.periodStart, period_end: q.periodEnd }) }
    )
    return data
  },

  async pipelineFunnel(workspaceId: string): Promise<Envelope<IPipelineFunnelResponse>> {
    const { data } = await api.get<Envelope<IPipelineFunnelResponse>>(
      `${base}/pipeline-funnel`,
      { params: { workspace_id: workspaceId } }
    )
    return data
  },

  async workspaces(organizationId: string): Promise<Envelope<IWorkspacesListResponse>> {
    const { data } = await api.get<Envelope<IWorkspacesListResponse>>(
      `${base}/workspaces`,
      { params: { organization_id: organizationId } }
    )
    return data
  },

  async addressabilityFunnel(q: IAddressabilityFunnelQuery): Promise<Envelope<IAddressabilityFunnelResponse>> {
    const { data } = await api.get<Envelope<IAddressabilityFunnelResponse>>(
      `${base}/addressability-funnel`,
      { params: toParams({ workspace_id: q.workspaceId, snapshot_start: q.snapshotStart, snapshot_end: q.snapshotEnd }) }
    )
    return data
  },

  async policyOptants(workspaceId: string): Promise<Envelope<IPolicyOptantsResponse>> {
    const { data } = await api.get<Envelope<IPolicyOptantsResponse>>(
      `${base}/policy-optants`,
      { params: { workspace_id: workspaceId } }
    )
    return data
  },

  async movements(q: IMovementsQuery): Promise<Envelope<IMovementsResponse>> {
    const { data } = await api.get<Envelope<IMovementsResponse>>(
      `${base}/movements`,
      { params: toParams({ workspace_id: q.workspaceId, period_start: q.periodStart, period_end: q.periodEnd }) }
    )
    return data
  },

  async alerts(workspaceId: string): Promise<Envelope<IAlertsResponse>> {
    const { data } = await api.get<Envelope<IAlertsResponse>>(
      `${base}/alerts`,
      { params: { workspace_id: workspaceId } }
    )
    return data
  },

  async caseDrilldown(workspaceId: string, beneficiarioTaxPayerId: string): Promise<Envelope<ICaseDrilldownResponse>> {
    const { data } = await api.get<Envelope<ICaseDrilldownResponse>>(
      `${base}/case/${encodeURIComponent(beneficiarioTaxPayerId)}`,
      { params: { workspace_id: workspaceId } }
    )
    return data
  },

  async movementsDrilldown(q: {
    workspaceId: string
    movementType?: string
    status?: string
    limit?: number
    offset?: number
  }): Promise<Envelope<IMovementsDrilldownResponse>> {
    const { data } = await api.get<Envelope<IMovementsDrilldownResponse>>(
      `${base}/movements-drilldown`,
      {
        params: toParams({
          workspace_id: q.workspaceId,
          movement_type: q.movementType,
          status: q.status,
          limit: q.limit !== undefined ? String(q.limit) : undefined,
          offset: q.offset !== undefined ? String(q.offset) : undefined,
        }),
      }
    )
    return data
  },

  async exceptionsDrilldown(q: { workspaceId: string; limit?: number; offset?: number }): Promise<Envelope<IExceptionsDrilldownResponse>> {
    const { data } = await api.get<Envelope<IExceptionsDrilldownResponse>>(
      `${base}/exceptions-drilldown`,
      {
        params: toParams({
          workspace_id: q.workspaceId,
          limit: q.limit !== undefined ? String(q.limit) : undefined,
          offset: q.offset !== undefined ? String(q.offset) : undefined,
        }),
      }
    )
    return data
  },

  async nonOptantsDrilldown(q: { workspaceId: string; limit?: number; offset?: number }): Promise<Envelope<INonOptantsDrilldownResponse>> {
    const { data } = await api.get<Envelope<INonOptantsDrilldownResponse>>(
      `${base}/non-optants-drilldown`,
      {
        params: toParams({
          workspace_id: q.workspaceId,
          limit: q.limit !== undefined ? String(q.limit) : undefined,
          offset: q.offset !== undefined ? String(q.offset) : undefined,
        }),
      }
    )
    return data
  },

  async maioridadeDrilldown(q: { workspaceId: string; warningDays?: number }): Promise<Envelope<IMaioridadeDrilldownResponse>> {
    const { data } = await api.get<Envelope<IMaioridadeDrilldownResponse>>(
      `${base}/maioridade-drilldown`,
      {
        params: toParams({
          workspace_id: q.workspaceId,
          warning_days: q.warningDays !== undefined ? String(q.warningDays) : undefined,
        }),
      }
    )
    return data
  },

  async vigenciasDrilldown(q: { workspaceId: string; limit?: number; offset?: number }): Promise<Envelope<IVigenciasDrilldownResponse>> {
    const { data } = await api.get<Envelope<IVigenciasDrilldownResponse>>(
      `${base}/vigencias-drilldown`,
      {
        params: toParams({
          workspace_id: q.workspaceId,
          limit: q.limit !== undefined ? String(q.limit) : undefined,
          offset: q.offset !== undefined ? String(q.offset) : undefined,
        }),
      }
    )
    return data
  },

  async addressabilityLossesDrilldown(q: {
    workspaceId: string
    category?: 'cargo' | 'regiao_lotacao'
    limit?: number
    offset?: number
  }): Promise<Envelope<IAddressabilityLossesDrilldownResponse>> {
    const { data } = await api.get<Envelope<IAddressabilityLossesDrilldownResponse>>(
      `${base}/addressability-losses-drilldown`,
      {
        params: toParams({
          workspace_id: q.workspaceId,
          category: q.category,
          limit: q.limit !== undefined ? String(q.limit) : undefined,
          offset: q.offset !== undefined ? String(q.offset) : undefined,
        }),
      }
    )
    return data
  },

  async dsarExport(body: IDsarRequest): Promise<IDsarResponse> {
    // Admin-only endpoint; response is NOT wrapped in governance envelope
    // (sits under /admin/lgpd/dsar per P1 Phase 3 Task 3.11).
    const { data } = await api.post<{ data: IDsarResponse }>('/admin/lgpd/dsar', body)
    return data.data
  },
}
